import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { NeedCard } from '../components/custom/NeedCard';
import { needsAPI } from '../lib/api';
import { Filter, Loader2, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const positions = [
  { value: 'all', label: 'Todas las posiciones' },
  { value: 'GK', label: 'Portera' },
  { value: 'DEF', label: 'Defensa' },
  { value: 'MID', label: 'Mediocampista' },
  { value: 'FWD', label: 'Delantera' },
];

const types = [
  { value: 'all', label: 'Cualquier tipo' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'loan', label: 'Préstamo' },
  { value: 'both', label: 'Ambos' },
];

const priorities = [
  { value: 'all', label: 'Cualquier prioridad' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low', label: 'Baja' },
];

export const NeedsPage = () => {
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    position: 'all',
    type: 'all',
    priority: 'all',
  });

  useEffect(() => {
    loadNeeds();
  }, [filters]);

  const loadNeeds = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.position !== 'all') params.position = filters.position;
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.priority !== 'all') params.priority = filters.priority;
      
      const response = await needsAPI.getAll(params);
      setNeeds(response.data);
    } catch (error) {
      console.error('Error loading needs:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      position: 'all',
      type: 'all',
      priority: 'all',
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="needs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 uppercase tracking-tight">
              Mis Necesidades
            </h1>
            <p className="text-slate-500 mt-1">
              Gestiona las necesidades de fichaje que has publicado
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select
              value={filters.position}
              onValueChange={(value) => setFilters({ ...filters, position: value })}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="filter-position">
                <SelectValue placeholder="Posición" />
              </SelectTrigger>
              <SelectContent>
                {positions.map((pos) => (
                  <SelectItem key={pos.value} value={pos.value}>
                    {pos.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.type}
              onValueChange={(value) => setFilters({ ...filters, type: value })}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="filter-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.priority}
              onValueChange={(value) => setFilters({ ...filters, priority: value })}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="filter-priority">
                <SelectValue placeholder="Prioridad" />
              </SelectTrigger>
              <SelectContent>
                {priorities.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters">
              <Filter className="w-4 h-4 mr-2" />
              Limpiar
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : needs.length > 0 ? (
          <div className="space-y-4">
            {needs.map((need) => (
              <NeedCard key={need.id} need={need} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-slate-600 uppercase">
              No hay necesidades
            </h3>
            <p className="text-slate-500 mt-2">
              Aún no has publicado ninguna necesidad de fichaje
            </p>
            <Button onClick={clearFilters} className="mt-4" variant="outline">
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NeedsPage;
